import { FC } from 'react';
import { useSnackbar } from 'notistack';
import { Fade, Button, ButtonGroup, Box } from '@mui/material';
import { Modal, Loader } from '../../../../components';
import { IClientDetail } from '../../../../models';
import { Prompt, useHistory } from 'react-router-dom';
import { Save, Close } from '@mui/icons-material';
import { deepEqual } from 'fast-equals';
import { Formik, Form } from 'formik';
import { clientDetailSchema } from './add-edit-client';
import { ContactInfoTab } from './contact-info-tab';
import { createClient } from '../../../../fetch';

interface IAddBuildModal {
  open: boolean;
  onClose: () => void;
  handleSave: (newClientId: number) => Promise<void>;
  personId: string;
  name: string;
  email: string;
  phoneNumber: string;
  agentId: string | number;
  contractName: string;
  clientDetail: IClientDetail;
}

export const AdditionalContactCreateUserModal: FC<IAddBuildModal> = ({
  open,
  onClose,
  handleSave,
  personId,
  name,
  email,
  phoneNumber,
  agentId,
  contractName,
  clientDetail
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const handleClose = () => {
    onClose();
  };

  const firstAndLastName = () => {
    const nameArray = name?.split(' ') ?? [];
    return {
      firstName: nameArray[0] ?? '',
      lastName: nameArray.slice(1).join(' ') ?? ''
    };
  };

  const names = firstAndLastName();
  return (
    <Modal open={open} onClose={handleClose} maxWidth="xs" title="Create New User">
      <Fade in={open}>
        <Box marginTop={'.5rem'}>
          <Formik
            initialValues={{
              isActive: true,
              contactInformation: {
                agentId: agentId ?? '',
                firstName: names?.firstName,
                lastName: names?.lastName,
                contractName: contractName ?? '',
                address: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                email: email ?? '',
                phoneNumber: phoneNumber ?? '',
                buyerHasAgent: false,
                agentFirstName: '',
                agentLastName: '',
                agentEmail: '',
                agentPhone: '',
                agentSellingCompany: ''
              },
              additionalContacts: []
            }}
            enableReinitialize={true}
            validationSchema={clientDetailSchema}
            onSubmit={async (values, actions) => {
              try {
                const data: IClientDetail = {
                  ...values,
                  contactInformation: {
                    ...values.contactInformation,
                    agentId: values.contactInformation.agentId
                      ? Number(values.contactInformation.agentId)
                      : null
                  }
                };
                const newPersonId = await createClient(data);
                // need to reset form after save to prevent unsaved changes prompt
                actions.resetForm();
                handleSave(newPersonId);
                handleClose();
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? 'Something went wrong. Purchaser was not created.',
                  {
                    variant: 'error'
                  }
                );
              }
            }}
          >
            {({
              isSubmitting,
              values,
              initialValues,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              dirty,
              isValid,
              errors
            }) => {
              return (
                <Form onSubmit={handleSubmit} autoComplete="none">
                  {isSubmitting && <Loader type="fullscreen" position="centered" />}
                  <Prompt
                    when={!deepEqual(initialValues, values)}
                    message="You have unsaved changes, are you sure you want to exit?"
                  />

                  <ContactInfoTab
                    isModal={true}
                    userId={undefined}
                    showHasAgent={false}
                    clientDetail={clientDetail}
                  />
                  <Box mt={1}>
                    <ButtonGroup>
                      <Button
                        disabled={
                          !dirty ||
                          isSubmitting ||
                          !isValid ||
                          (values?.contactInformation?.agentPhone === '' &&
                            values?.contactInformation?.buyerHasAgent)
                        }
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        disabled={isSubmitting}
                        startIcon={<Close />}
                        onClick={() => {
                          history.push('/sales/contracting/purchasers');
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Fade>
    </Modal>
  );
};
